import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "../lib/api";
import { CheckCircle2, Loader2, Clock, XCircle, Car, Package, MapPin, Navigation } from "lucide-react";

const eur = (n) => `${(Number(n) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

export default function RideConfirmed() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | confirmed | pending | error
  const [ride, setRide] = useState(null);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let attempts = 0;
    let stop = false;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/rides/eu-checkout/status/${sessionId}`);
        setRide(data);
        if (data.payment_status === "paid") { setState("confirmed"); return; }
      } catch { /* keep polling */ }
      if (stop) return;
      if (attempts >= 12) { setState("pending"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [sessionId]);

  const isDeliv = ride?.type === "delivery";

  return (
    <div className="min-h-screen bg-[#FFF9F2] flex items-center justify-center px-5 py-10">
      <motion.div
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        data-testid="ride-confirmed" className="max-w-md w-full rounded-3xl p-8 bg-white border border-[#E2DFD8] shadow-[0_8px_40px_rgba(0,0,0,0.06)] text-center">
        {state === "checking" && (
          <>
            <Loader2 className="w-14 h-14 mx-auto animate-spin text-[#F26419]" />
            <h1 className="mt-5 font-head font-black text-2xl text-[#1A1A1A]">Vérification du paiement…</h1>
            <p className="mt-2 text-[#75807A] text-sm">Un instant, nous confirmons votre course.</p>
          </>
        )}
        {state === "confirmed" && (
          <>
            <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center bg-[#16a34a]">
              <CheckCircle2 className="w-9 h-9 text-white" />
            </div>
            <h1 data-testid="ride-confirmed-title" className="mt-5 font-head font-black text-2xl text-[#1A1A1A]">Course confirmée ✅</h1>
            <p className="mt-2 text-[#75807A] text-sm">Paiement reçu. Nous recherchons un chauffeur pour vous.</p>
            {ride && (
              <div className="mt-6 rounded-2xl p-4 text-left space-y-2 bg-[#FFF3E9] border border-[#F26419]/30">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-[#75807A] flex items-center gap-1.5">
                    {isDeliv ? <Package className="w-4 h-4 text-[#16a34a]" /> : <Car className="w-4 h-4 text-[#2563EB]" />}
                    {isDeliv ? "Colis Vélo" : "Voiture"}
                  </span>
                  <span data-testid="ride-confirmed-amount" className="font-head font-black text-[#1A1A1A]">{eur(ride.price)}</span>
                </div>
                <div className="flex justify-between gap-3 text-sm"><span className="text-[#75807A] shrink-0 flex items-center gap-1"><Navigation className="w-3.5 h-3.5" /> Départ</span><span className="font-medium text-right truncate">{ride.pickup}</span></div>
                <div className="flex justify-between gap-3 text-sm"><span className="text-[#75807A] shrink-0 flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-[#D94F2B]" /> Arrivée</span><span className="font-medium text-right truncate">{ride.destination}</span></div>
              </div>
            )}
            <button data-testid="ride-confirmed-home" onClick={() => navigate("/passenger")} className="mt-6 w-full h-13 py-3.5 rounded-xl font-head font-black text-white bg-[#F26419] hover:bg-[#D85312] transition-colors">
              Suivre ma course
            </button>
          </>
        )}
        {state === "pending" && (
          <>
            <Clock className="w-14 h-14 mx-auto text-[#F26419]" />
            <h1 className="mt-5 font-head font-black text-2xl text-[#1A1A1A]">Paiement en cours de confirmation</h1>
            <p className="mt-2 text-[#75807A] text-sm">Si vous avez été débité, votre course apparaîtra dans votre historique dans un instant.</p>
            <button data-testid="ride-pending-home" onClick={() => navigate("/passenger")} className="mt-6 w-full h-12 rounded-xl font-bold text-white bg-[#1A1A1A]">Aller à mon espace</button>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="w-14 h-14 mx-auto text-[#75807A]" />
            <h1 className="mt-5 font-head font-black text-2xl text-[#1A1A1A]">Impossible de vérifier</h1>
            <p className="mt-2 text-[#75807A] text-sm">Contactez le support si vous avez été débité.</p>
            <button onClick={() => navigate("/passenger")} className="mt-6 w-full h-12 rounded-xl font-bold text-white bg-[#1A1A1A]">Retour</button>
          </>
        )}
      </motion.div>
    </div>
  );
}
